import { useState, useEffect } from 'react'
import type { ChainConfig } from '@/types'
import { DEFAULT_CHAIN_CONFIG } from '@/types'

interface DebugPanelProps {
    config: ChainConfig
    onConfigChange: (config: ChainConfig) => void
}

interface SliderProps {
    label: string
    value: number
    min: number
    max: number
    step: number
    onChange: (value: number) => void
}

function Slider({ label, value, min, max, step, onChange }: SliderProps) {
    return (
        <div className="flex items-center gap-2 text-xs">
            <label className="w-32 shrink-0 text-foreground/70">{label}</label>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="flex-1 h-1 accent-primary"
            />
            <span className="w-12 text-right font-mono text-foreground/50">
                {value}
            </span>
        </div>
    )
}

export function DebugPanel({ config, onConfigChange }: DebugPanelProps) {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'd' || e.key === 'D') {
                setVisible((v) => !v)
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    const update = (key: keyof ChainConfig, value: number) => {
        onConfigChange({ ...config, [key]: value })
    }

    const reset = () => {
        onConfigChange({ ...DEFAULT_CHAIN_CONFIG })
    }

    if (!visible) {
        return (
            <button
                onClick={() => setVisible(true)}
                className="fixed bottom-4 left-4 bg-card/95 backdrop-blur border rounded-lg px-3 py-2 shadow-lg z-50 transition-colors duration-700 text-[10px] text-muted-foreground hover:text-foreground cursor-pointer"
            >
                Settings <kbd className="px-1 py-0.5 rounded bg-muted text-[10px]">D</kbd>
            </button>
        )
    }

    return (
        <div className="fixed bottom-4 left-4 bg-card/95 backdrop-blur border rounded-lg p-4 w-80 shadow-lg z-50 transition-colors duration-700">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">
                    Chain Physics Debug
                </h3>
                <div className="flex items-center gap-2">
                    <button
                        onClick={reset}
                        className="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded border"
                    >
                        Reset
                    </button>
                    <button
                        onClick={() => setVisible(false)}
                        className="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded border"
                    >
                        Close
                    </button>
                </div>
            </div>
            <div className="space-y-2">
                <Slider
                    label="Bead Count"
                    value={config.beadCount}
                    min={5}
                    max={30}
                    step={1}
                    onChange={(v) => update('beadCount', v)}
                />
                <Slider
                    label="Bead Radius"
                    value={config.beadRadius}
                    min={2}
                    max={10}
                    step={0.5}
                    onChange={(v) => update('beadRadius', v)}
                />
                <Slider
                    label="Handle Radius"
                    value={config.handleRadius}
                    min={4}
                    max={20}
                    step={1}
                    onChange={(v) => update('handleRadius', v)}
                />
                <Slider
                    label="Segment Length"
                    value={config.segmentLength}
                    min={6}
                    max={30}
                    step={1}
                    onChange={(v) => update('segmentLength', v)}
                />
                <Slider
                    label="Gravity"
                    value={config.gravity}
                    min={100}
                    max={3000}
                    step={50}
                    onChange={(v) => update('gravity', v)}
                />
                <Slider
                    label="Damping"
                    value={config.damping}
                    min={0.9}
                    max={0.999}
                    step={0.001}
                    onChange={(v) => update('damping', v)}
                />
                <Slider
                    label="Iterations"
                    value={config.constraintIterations}
                    min={1}
                    max={15}
                    step={1}
                    onChange={(v) => update('constraintIterations', v)}
                />
                <Slider
                    label="Pull Threshold"
                    value={config.pullThreshold}
                    min={20}
                    max={200}
                    step={5}
                    onChange={(v) => update('pullThreshold', v)}
                />
                <Slider
                    label="Resistance Curve"
                    value={config.pullResistanceCurve}
                    min={1}
                    max={5}
                    step={0.1}
                    onChange={(v) => update('pullResistanceCurve', v)}
                />
            </div>
        </div>
    )
}
